/**
 * Code examples for the API documentation page, in three languages.
 *
 * PORTED FROM middleware-01, DELIBERATELY (operator directive,
 * 2026-08-21 : "These tools should reflect what we have done there
 * because middleware reflects the tracking system api docs.
 * Consistency is important."). The three builders below reproduce the
 * output shapes of buildCurl, buildJavascript and buildPython in
 * middleware-01/src/handlers/api-docs/render.ts line by line, so a
 * partner reading our documentation and the tracking documentation
 * sees the same example in the same form.
 *
 * BUILT, NEVER HAND-WRITTEN. Fourteen endpoints times three languages
 * is forty-two blocks, and a hand-written set drifts the moment a
 * request body changes : the example would keep describing a field the
 * endpoint no longer takes, with nothing to notice. Deriving every
 * example from the SAME endpoint definition that produces the request
 * body documentation means an edit reaches all three at once.
 *
 * Ours are simpler than middleware's in one way and that is not an
 * omission : every Trustico® tools endpoint is public and unkeyed, so
 * there is no API key, no idempotency key and no per-endpoint header
 * set. Content-Type is the only header any of them needs, and only
 * when there is a body to send.
 */

/**
 * Indent every line after the first.
 *
 * A JSON body is pretty-printed from column zero, and dropping it into
 * a nested position leaves its first line correct and every following
 * line hanging at the left margin.
 *
 * @param {string} text
 * @param {string} pad
 * @returns {string}
 */
function indentBlock(text, pad) {
  const lines = text.split("\n");
  for (let i = 1; i < lines.length; i++) lines[i] = pad + lines[i];
  return lines.join("\n");
}

/**
 * A request body as a PYTHON literal, not as JSON.
 *
 * JSON AND PYTHON DISAGREE ON THREE WORDS, and interpolating
 * JSON.stringify output into a `json=` argument published examples that
 * fail on the first line a partner runs :
 *
 *   NameError: name 'true' is not defined
 *
 * It parses, because `true` is a legal Python identifier ; it dies at
 * runtime, when the name turns out not to exist. Two documented
 * endpoints carry booleans - the CAA generator names five and the DNS
 * Stamp generator three - so both of their Python examples were broken,
 * directly beneath a sentence promising a ready-to-run request. Exactly
 * the fault this module was written to remove from the curl examples,
 * one language along.
 *
 * Strings still go through JSON.stringify : Python and JSON agree on
 * quoting and on every escape that appears in these bodies, and
 * re-implementing string escaping to avoid one call is how a subtler
 * version of this same bug gets written.
 *
 * @param {*} value
 * @param {string} indent the padding this value's CONTENTS sit at
 * @returns {string}
 */
function pythonLiteral(value, indent) {
  if (value === null) return "None";
  if (value === true) return "True";
  if (value === false) return "False";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return JSON.stringify(value);

  const inner = indent + "    ";
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    const items = value.map((item) => inner + pythonLiteral(item, inner));
    return `[\n${items.join(",\n")}\n${indent}]`;
  }

  const entries = Object.entries(value);
  if (entries.length === 0) return "{}";
  const pairs = entries.map(
    ([key, item]) =>
      `${inner}${JSON.stringify(key)}: ${pythonLiteral(item, inner)}`
  );
  return `{\n${pairs.join(",\n")}\n${indent}}`;
}

/**
 * The headers an example needs.
 *
 * Content-Type ONLY WHEN THERE IS A BODY. A GET with a JSON
 * Content-Type describes a request that carries no JSON, which is the
 * sort of copied-and-pasted detail that outlives the page it came
 * from.
 *
 * @param {object} endpoint
 * @returns {Array<[string, string]>}
 */
function headersFor(endpoint) {
  return endpoint.requestBody ? [["Content-Type", "application/json"]] : [];
}

/**
 * The absolute URL an example calls.
 *
 * @param {object} endpoint
 * @param {string} baseUrl
 * @returns {string}
 */
function endpointUrl(endpoint, baseUrl) {
  return `${baseUrl}${endpoint.path}`;
}

/** A curl invocation, one flag per continued line. */
export function buildCurl(endpoint, baseUrl) {
  const lines = [
    `curl -X ${endpoint.method} ${endpointUrl(endpoint, baseUrl)}`,
  ];
  for (const [name, value] of headersFor(endpoint)) {
    lines.push(`-H "${name}: ${value}"`);
  }
  if (endpoint.requestBody) {
    lines.push(`-d '${JSON.stringify(endpoint.requestBody, null, 2)}'`);
  }
  return lines.join(" \\\n  ");
}

/**
 * A browser or Node fetch call.
 *
 * THE METHOD IS NOT CONDITIONAL ON THERE BEING A BODY. This gated the
 * whole init object on `headers.length > 0 || body`, and `method` lives
 * inside it - so a bodyless request published a bare one-argument
 * fetch, which performs a GET whatever the endpoint's method says. For
 * a GET that is right and is why it went unnoticed ; for a bodyless
 * DELETE the JavaScript tab would have sent the wrong verb while the
 * curl tab beside it correctly read -X DELETE and the Python tab
 * correctly called requests.delete. Three tabs disagreeing, with the
 * wrong one being the tab most likely to be pasted into a project.
 *
 * Not hypothetical : DELETE /api/issuance/sessions/:sessionId takes no
 * body at all, and GET /api/dnsstamp/protocols is registered too.
 * Neither is documented yet, and documenting either is exactly when
 * this would have shipped.
 */
export function buildJavascript(endpoint, baseUrl) {
  let out = `const response = await fetch("${endpointUrl(endpoint, baseUrl)}"`;
  const headers = headersFor(endpoint);
  const body = endpoint.requestBody;
  // A plain GET needs no init object at all ; anything else must state
  // its method even when it carries nothing.
  if (headers.length > 0 || body || endpoint.method !== "GET") {
    out += `, {\n  method: "${endpoint.method}",\n`;
    if (headers.length > 0) {
      out += "  headers: {\n";
      for (const [name, value] of headers) {
        out += `    "${name}": "${value}",\n`;
      }
      out += "  },\n";
    }
    if (body) {
      out += `  body: JSON.stringify(${indentBlock(
        JSON.stringify(body, null, 2),
        "  "
      )}),\n`;
    }
    out += "}";
  }
  out += ");\n\nconst data = await response.json();";
  return out;
}

/** A requests call. */
export function buildPython(endpoint, baseUrl) {
  let out = `import requests\n\nresponse = requests.${endpoint.method.toLowerCase()}(\n`;
  out += `    "${endpointUrl(endpoint, baseUrl)}",\n`;
  const headers = headersFor(endpoint);
  const body = endpoint.requestBody;
  if (headers.length > 0) {
    out += "    headers={\n";
    for (const [name, value] of headers) {
      out += `        "${name}": "${value}",\n`;
    }
    out += "    },\n";
  }
  if (body) {
    // A Python literal, never JSON : see pythonLiteral above.
    out += `    json=${pythonLiteral(body, "    ")},\n`;
  }
  out += ")\n\ndata = response.json()";
  return out;
}

/**
 * The three languages a reader can switch between, in the order
 * middleware-01 shows them. curl leads because it is the one a partner
 * can paste into a terminal without a project around it.
 */
export const EXAMPLE_LANGUAGES = [
  { id: "curl", label: "curl", build: buildCurl },
  { id: "javascript", label: "JavaScript", build: buildJavascript },
  { id: "python", label: "Python", build: buildPython },
];
